import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ButtonBase, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import type { LocationStatus, MaturityDescriptor, PourSummary } from '../../../services/pours';
import {
  channelTypeLabel,
  isAmbientChannel,
  isHumidityLocation,
  isSiteConditionsChannel,
  locationFunctionLabel,
  orderByFunction,
} from '../../../utils/locationChannels';
import { locationHasMaturity } from '../../../utils/locationMaturity';
import { formatHalfUp, formatMaturity, maturityShortLabel } from '../../../utils/maturityDisplay';
import { formatAgo, type TranslateFn } from '../../../utils/relativeTime';
import { loggerCodePrefix, nextTarget, type LocationHealth } from './mobileDashboardModel';
import { readLine, strengthOf } from './MobileLocationCard';
import { breachText, limitNumber, locationLabel, staleOverText } from './mobileText';
import { SOFT_CARD_SX } from './PourStatusStrip';
import { PILL_TONES } from './StatusPill';

const WARNING_EDGE = '#d88c00';

/** The group headings, translated by name. */
const groupHeading = (label: string, t: TranslateFn): string => {
  switch (label) {
    case 'Concrete Maturity':
      return t('mobile.dashboard.overview.groupMaturity', { defaultValue: 'Concrete Maturity' });
    case 'Temperature Only':
      return t('mobile.dashboard.overview.groupTemperature', { defaultValue: 'Temperature Only' });
    case 'Ambient Temp & Humidity':
      return t('mobile.dashboard.overview.groupAmbient', { defaultValue: 'Ambient Temp & Humidity' });
    case 'Weather Station':
      return t('mobile.dashboard.overview.groupWeather', { defaultValue: 'Weather Station' });
    default:
      return label;
  }
};

/** The tone of a card's left edge and problem line; null when all is well. */
const cardTone = (health: LocationHealth): 'error' | 'warning' | null => {
  switch (health.alert) {
    case 'over_limit':
    case 'under_limit':
    case 'offline':
      return 'error';
    case 'delayed':
    case 'sensor':
      return 'warning';
    default:
      return health.evaporationOver ? 'error' : null;
  }
};

const worseTone = (a: 'error' | 'warning' | null, b: 'error' | 'warning' | null) =>
  a === 'error' || b === 'error' ? 'error' : a ?? b;

export interface LocationCardsContext {
  pour: PourSummary;
  meta: MaturityDescriptor;
  strengthUnit: string;
  maturityFunction: boolean;
  /** The pour calculates the evaporation rate: concrete cards show it. */
  evaporationFunction: boolean;
  healthById: ReadonlyMap<string, LocationHealth>;
  refNow: number;
  now: number;
  onSelect: (locationId: string) => void;
}

/** One labelled reading in a card's row of columns. */
interface Column {
  key: string;
  label: string;
  value: string;
  unit?: string;
  sub?: string | null;
  bad?: boolean;
  /** A wider share of the row (strength). */
  wide?: boolean;
}

/** The columns a section shows: the same on every card in it, so a list
 *  reads down like the desktop table. */
interface SectionColumns {
  differential: boolean;
  evaporation: boolean;
  maturity: boolean;
  /** "Strength", or the maturity name without a usable curve. */
  maturityLabel: string;
}

const live = (health: LocationHealth) => !['offline', 'scheduled', 'awaiting'].includes(health.alert);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Line 2 under a name: the problem in words, else "Temperature · 5021-1". */
const statusLine = (
  loc: LocationStatus,
  health: LocationHealth,
  ctx: LocationCardsContext,
  t: TranslateFn,
): { text: string; color: string } => {
  const tone = cardTone(health);
  if (health.breaches.length > 0) return { text: breachText(health.breaches[0], t, ctx.refNow), color: PILL_TONES.error.fg };
  if (health.staleOver) {
    return {
      text: staleOverText(health.staleOver, t, health.staleOver.at !== null ? formatAgo(health.staleOver.at, ctx.now, t) : ''),
      color: PILL_TONES.error.fg,
    };
  }
  if (health.evaporationOver && live(health)) {
    return { text: t('mobile.dashboard.card.evaporationOver', { defaultValue: 'Evaporation over limit' }), color: PILL_TONES.error.fg };
  }
  if (health.alert === 'sensor') {
    return {
      text: t('mobile.dashboard.card.sensorLogger', { defaultValue: 'Sensor issue · Logger {{code}}', code: loc.logger_code }),
      color: PILL_TONES.warning.fg,
    };
  }
  if (tone || health.alert === 'scheduled' || health.online.awaiting || health.alert === 'ended') {
    return readLine(loc, health, t, ctx.now, ctx.pour.timezone_iana);
  }
  return {
    text: [t(`mobile.dashboard.channel.${channelTypeLabel(loc)}`, { defaultValue: channelTypeLabel(loc) }), loc.logger_code]
      .filter(Boolean)
      .join(' · '),
    color: 'text.secondary',
  };
};

/** "Now": the newest reading, red while outside a limit; '-' while offline
 *  (the line under the name says when it last read). */
const nowColumn = (loc: LocationStatus, health: LocationHealth, t: TranslateFn, label?: string): Column => {
  const humidity = isHumidityLocation(loc);
  const value = health.alert === 'scheduled' || health.alert === 'offline' ? null : num(loc.current_temp);
  return {
    key: 'now',
    label: label ?? t('mobile.dashboard.card.now', { defaultValue: 'Now' }),
    value: value === null ? '-' : humidity ? String(Math.round(value)) : value.toFixed(1),
    unit: value === null ? undefined : humidity ? '%RH' : '°C',
    bad: health.alert === 'over_limit' || health.alert === 'under_limit',
  };
};

const maxColumn = (loc: LocationStatus, health: LocationHealth, t: TranslateFn): Column => {
  const humidity = isHumidityLocation(loc);
  const value = health.alert === 'scheduled' ? null : num(loc.max_temp);
  return {
    key: 'max',
    label: t('mobile.dashboard.card.max', { defaultValue: 'Max' }),
    value: value === null ? '-' : humidity ? String(Math.round(value)) : value.toFixed(1),
    unit: value === null ? undefined : humidity ? '%RH' : '°C',
  };
};

/** A concrete location's columns: Now, Max, then the section's
 *  Differential, Strength (or maturity) and Evaporation. */
const concreteColumns = (
  loc: LocationStatus,
  health: LocationHealth,
  cols: SectionColumns,
  ctx: LocationCardsContext,
  t: TranslateFn,
): Column[] => {
  const out = [nowColumn(loc, health, t), maxColumn(loc, health, t)];
  if (cols.differential) {
    const d = (loc.differentials ?? []).find((x) => num(x.value) !== null);
    out.push({
      key: 'differential',
      label: t('mobile.dashboard.card.differential', { defaultValue: 'Differential' }),
      value: d ? (d.value as number).toFixed(1) : '-',
      unit: d ? '°C' : undefined,
      sub: d && num(d.limit_value) !== null
        ? t('mobile.dashboard.card.diffLimit', { defaultValue: 'limit {{limit}} °C', limit: limitNumber(d.limit_value as number) })
        : null,
      bad: !!d?.over,
    });
  }
  if (cols.maturity) {
    const ea = num(loc.current_ea);
    let value = '-';
    let unit: string | undefined;
    let sub: string | null = null;
    if (locationHasMaturity(loc) && health.alert !== 'scheduled' && ea !== null) {
      const s = strengthOf(loc, ctx.pour, ctx.meta, ctx.strengthUnit);
      if (s.label === 'strength') {
        // "> 52.4 MPa": the unit drawn small after the number.
        const suffix = ` ${ctx.strengthUnit}`;
        value = s.value.endsWith(suffix) ? s.value.slice(0, -suffix.length) : s.value;
        unit = s.value.endsWith(suffix) ? ctx.strengthUnit : undefined;
      } else {
        value = formatHalfUp(ea, 1);
        sub = ctx.meta.unit;
      }
    }
    out.push({ key: 'maturity', label: cols.maturityLabel, value, unit, sub, wide: true });
  }
  if (cols.evaporation) {
    const rate = num(loc.evaporation_now);
    out.push({
      key: 'evaporation',
      label: t('mobile.dashboard.card.evaporation', { defaultValue: 'Evaporation' }),
      value: rate === null ? '-' : rate.toFixed(2),
      sub: rate === null ? null : 'kg/m²/h',
      bad: !!loc.evaporation_over,
    });
  }
  return out;
};

/** The readings in fixed labelled columns. */
const Columns: React.FC<{ columns: Column[] }> = ({ columns }) => (
  <Box
    data-testid="card-columns"
    sx={{
      display: 'grid',
      gridTemplateColumns: columns.map((c) => (c.wide ? 'minmax(0, 1.3fr)' : 'minmax(0, 1fr)')).join(' '),
      gap: 1,
      mt: 1.5,
    }}
  >
    {columns.map((c, i) => (
      <Box key={c.key} data-testid={`col-${c.key}`} sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.71875rem', fontWeight: 500, color: 'text.secondary' }}>
          {c.label}
        </Typography>
        <Typography
          noWrap
          sx={{
            fontSize: i === 0 ? '1.375rem' : '1.125rem',
            fontWeight: 650,
            letterSpacing: '-0.01em',
            lineHeight: 1.25,
            color: c.bad ? PILL_TONES.error.fg : 'text.primary',
          }}
        >
          {c.value}
          {c.unit && (
            <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', ml: '2px' }}>
              {c.unit}
            </Box>
          )}
        </Typography>
        {c.sub && (
          <Typography noWrap sx={{ fontSize: '0.71875rem', color: c.bad ? PILL_TONES.error.fg : 'text.secondary' }}>
            {c.sub}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

/** The bar to the location's next target, with "Next: Stripping 25 MPa"
 *  and "1 of 3" under it. */
const NextTargetLine: React.FC<{ loc: LocationStatus; health: LocationHealth; ctx: LocationCardsContext }> = ({
  loc,
  health,
  ctx,
}) => {
  const { t } = useTranslation();
  if (!ctx.maturityFunction || !locationHasMaturity(loc) || health.alert === 'scheduled') return null;
  const next = nextTarget(loc, ctx.meta, { strengthUnit: ctx.strengthUnit, timezoneIana: ctx.pour.timezone_iana });
  if (!next) return null;
  const pct = Math.round((next.allReached ? 1 : next.progress ?? 0) * 100);
  // "Stripping 25.0 MPa", or "Strip 150.0 hrs" for a target set by maturity.
  const label = [next.name, next.strengthText || (next.maturity !== null ? formatMaturity(next.maturity, ctx.meta) : '')]
    .filter(Boolean)
    .join(' ');
  return (
    <Box data-testid="card-target" sx={{ mt: 1.5 }}>
      <Box
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 3, overflow: 'hidden' }}
      >
        <Box sx={{ width: `${Math.max(pct, 2)}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 3 }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.625, fontSize: '0.75rem', color: 'text.secondary' }}>
        {next.allReached ? (
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('mobile.controlPanel.target.allReached', {
              count: next.total,
              defaultValue_one: 'Target reached',
              defaultValue_other: 'All {{count}} targets reached',
            })}
          </Box>
        ) : (
          <>
            <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('mobile.controlPanel.target.next', { defaultValue: 'Next:' })}{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {label}
              </Box>
            </Box>
            <Box component="span" sx={{ flexShrink: 0 }}>
              {t('mobile.dashboard.strip.reachedCount', { defaultValue: '{{reached}} of {{total}}', reached: next.reached, total: next.total })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

/** A tappable soft card: name and its state line, a chevron, then the
 *  readings. A problem colours the left edge. */
const CardShell: React.FC<{
  name: string;
  line: { text: string; color: string };
  tone: 'error' | 'warning' | null;
  locationId: string;
  ctx: LocationCardsContext;
  children: React.ReactNode;
}> = ({ name, line, tone, locationId, ctx, children }) => {
  const { t } = useTranslation();
  return (
    <ButtonBase
      onClick={() => ctx.onSelect(locationId)}
      aria-label={t('mobile.dashboard.card.open', { defaultValue: 'Open {{name}}', name })}
      data-testid="location-row"
      data-location-id={locationId}
      sx={{
        ...SOFT_CARD_SX,
        width: '100%',
        display: 'block',
        textAlign: 'left',
        borderLeft: tone ? `4px solid ${tone === 'error' ? PILL_TONES.error.fg : WARNING_EDGE}` : undefined,
        pl: tone ? '14px' : 2,
        pr: 1.5,
        py: 2,
        mb: 1.5,
        transition: 'box-shadow 0.15s',
        '&:hover, &.Mui-focusVisible': { boxShadow: '0 2px 8px rgba(16,24,40,.12)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.96875rem', fontWeight: 600, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
            {name}
          </Typography>
          <Typography
            data-testid="card-status"
            sx={{
              fontSize: '0.75rem',
              mt: 0.25,
              color: line.color,
              fontWeight: line.color === 'text.secondary' ? 400 : 600,
              overflowWrap: 'anywhere',
            }}
          >
            {line.text}
          </Typography>
        </Box>
        <ChevronRightIcon aria-hidden sx={{ fontSize: 20, color: '#b5bac0', flexShrink: 0, mt: '1px' }} />
      </Box>
      {children}
    </ButtonBase>
  );
};

/** One location as a card: concrete (Now, Max, Differential, Strength,
 *  Evaporation as the section shows) or a site channel (Now, Max). A
 *  weather station is the pour's site weather, never a location card. */
const LocationCard: React.FC<{ loc: LocationStatus; health: LocationHealth; cols: SectionColumns; ctx: LocationCardsContext }> = ({
  loc,
  health,
  cols,
  ctx,
}) => {
  const { t } = useTranslation();
  let columns: Column[];
  if (isSiteConditionsChannel(loc)) {
    columns = [nowColumn(loc, health, t), maxColumn(loc, health, t)];
  } else {
    columns = concreteColumns(loc, health, cols, ctx, t);
  }
  return (
    <CardShell name={locationLabel(loc)} line={statusLine(loc, health, ctx, t)} tone={cardTone(health)} locationId={loc.id} ctx={ctx}>
      <Columns columns={columns} />
      <NextTargetLine loc={loc} health={health} ctx={ctx} />
    </CardShell>
  );
};

/** The name the channels of one logger share ("East End" from "East End
 *  Temperature (Ch 1)" and "East End Humidity"); '' when none. */
const sharedName = (names: string[]): string => {
  if (names.length === 0) return '';
  let prefix = names[0];
  names.slice(1).forEach((n) => {
    while (prefix && !n.startsWith(prefix)) prefix = prefix.slice(0, -1);
  });
  return prefix.replace(/[\s(·-]+$/, '').trim();
};

/** An ambient Temp & Humidity logger: one card, one column per channel
 *  (temperatures first, then humidity), each with its max under it. It
 *  opens the first temperature channel. */
const LoggerCard: React.FC<{
  loggerCode: string;
  entries: { loc: LocationStatus; health: LocationHealth }[];
  ctx: LocationCardsContext;
}> = ({ loggerCode, entries, ctx }) => {
  const { t } = useTranslation();
  const temps = entries.filter((e) => !isHumidityLocation(e.loc));
  const rhs = entries.filter((e) => isHumidityLocation(e.loc));
  const ordered = [...temps, ...rhs];
  const column = (e: { loc: LocationStatus; health: LocationHealth }, many: boolean): Column => {
    const humidity = isHumidityLocation(e.loc);
    const word = humidity
      ? t('mobile.dashboard.card.humidity', { defaultValue: 'Humidity' })
      : t('mobile.dashboard.card.temperature', { defaultValue: 'Temperature' });
    const label = many
      ? t('mobile.dashboard.card.channelOf', { defaultValue: '{{word}} ch {{channel}}', word: humidity ? 'RH' : 'Temp', channel: e.loc.channel })
      : word;
    const max = e.health.alert === 'scheduled' ? null : num(e.loc.max_temp);
    return {
      ...nowColumn(e.loc, e.health, t, label),
      key: `ch${e.loc.channel}`,
      sub: max === null ? null : t('mobile.dashboard.card.maxValue', {
        defaultValue: 'max {{value}}',
        value: humidity ? `${Math.round(max)} %RH` : `${max.toFixed(1)} °C`,
      }),
    };
  };
  // The worst channel's line, so a problem on any channel shows.
  const worst = ordered.find((e) => cardTone(e.health) === 'error') ?? ordered.find((e) => cardTone(e.health)) ?? null;
  const tone = ordered.reduce<'error' | 'warning' | null>((acc, e) => worseTone(acc, cardTone(e.health)), null);
  const name = sharedName(ordered.map((e) => locationLabel(e.loc))) || loggerCode;
  return (
    <CardShell
      name={name}
      line={worst
        ? statusLine(worst.loc, worst.health, ctx, t)
        : { text: `${t('mobile.dashboard.card.tempHumidity', { defaultValue: 'Temp & humidity' })} · ${loggerCode}`, color: 'text.secondary' }}
      tone={tone}
      locationId={ordered[0].loc.id}
      ctx={ctx}
    >
      <Columns columns={[
        ...temps.map((e) => column(e, temps.length > 1)),
        ...rhs.map((e) => column(e, rhs.length > 1)),
      ]} />
    </CardShell>
  );
};

/**
 * The Overview's locations as soft cards, one section per monitoring
 * function in desktop order (Concrete Maturity, Temperature Only, Ambient
 * Temp & Humidity, Weather Station), each headed with its count. Every card
 * in a section shows the same columns, so the list reads down like the
 * desktop table. Problems show on their own card (coloured edge and words);
 * nothing is regrouped or summarised.
 */
export const LocationCards: React.FC<{ locations: ReadonlyArray<LocationStatus>; ctx: LocationCardsContext }> = ({
  locations,
  ctx,
}) => {
  const { t } = useTranslation();
  const { ordered } = orderByFunction([...locations]);
  const groups: { label: string; locs: LocationStatus[] }[] = [];
  ordered.forEach((loc) => {
    const label = locationFunctionLabel(loc);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.locs.push(loc);
    else groups.push({ label, locs: [loc] });
  });

  return (
    <Box data-testid="location-cards">
      {groups.map((group) => {
        const concrete = group.locs.filter((l) => !isSiteConditionsChannel(l));
        const sample = concrete.find((l) => locationHasMaturity(l));
        const strengthShown = !!sample && strengthOf(sample, ctx.pour, ctx.meta, ctx.strengthUnit).label === 'strength';
        const cols: SectionColumns = {
          differential: concrete.some((l) => (l.differentials?.length ?? 0) > 0),
          evaporation: ctx.evaporationFunction && concrete.some((l) => num(l.evaporation_now) !== null),
          maturity: ctx.maturityFunction && !!sample,
          maturityLabel: strengthShown
            ? t('mobile.controlPanel.tile.strength', { defaultValue: 'Strength' })
            : maturityShortLabel(ctx.meta),
        };

        // An ambient Temp & Humidity logger's channels share one card; an air
        // probe (no humidity on its logger) stays a card of its own.
        const cards: React.ReactNode[] = [];
        const done = new Set<string>();
        group.locs.forEach((loc) => {
          if (done.has(loc.id)) return;
          const health = ctx.healthById.get(loc.id);
          if (!health) return;
          if (isAmbientChannel(loc)) {
            const prefix = loggerCodePrefix(loc.logger_code);
            const siblings = group.locs.filter((l) => isAmbientChannel(l) && loggerCodePrefix(l.logger_code) === prefix);
            const entries = siblings
              .map((l) => ({ loc: l, health: ctx.healthById.get(l.id) }))
              .filter((e): e is { loc: LocationStatus; health: LocationHealth } => !!e.health);
            if (entries.length > 1 && entries.some((e) => isHumidityLocation(e.loc))) {
              entries.forEach((e) => done.add(e.loc.id));
              cards.push(<LoggerCard key={`logger-${prefix}`} loggerCode={prefix} entries={entries} ctx={ctx} />);
              return;
            }
          }
          done.add(loc.id);
          cards.push(<LocationCard key={loc.id} loc={loc} health={health} cols={cols} ctx={ctx} />);
        });

        return (
          <Box component="section" key={group.label} aria-label={groupHeading(group.label, t)} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 0.5, mb: 1 }}>
              <Typography
                component="h2"
                sx={{ fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#4a5056' }}
              >
                {groupHeading(group.label, t)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {t('mobile.dashboard.overview.locationCount', {
                  count: group.locs.length,
                  defaultValue_one: '{{count}} location',
                  defaultValue_other: '{{count}} locations',
                })}
              </Typography>
            </Box>
            {cards}
          </Box>
        );
      })}
    </Box>
  );
};

export default LocationCards;
